import { debounce } from 'lodash';
import React from 'react';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { useSearchReducer } from '@components/hooks/useSearchReducer';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import {
    CommunityDailyGoalRequestV3Dto,
    CommunityGoalAdminUpdateRequestDto,
    RecurrenceType
} from '@api/FitnessActivityClient';

interface GoalsSearchParams {
    page?: number;
    size?: number;
    search?: string;
    sort?: string;
    communityId?: number;
    type?: RecurrenceType;
}

export function useGoalsStore(communityId: number) {
    const { fitnessActivityClient, challengeClient } = injectTSDI(Api);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });

    const setSearchTerm = React.useMemo(
        () =>
            debounce((search?: string) => {
                dispatch({ type: 'search', search: search || '' });
            }, 300),
        []
    );

    const goals = useWrapRequest(
        async (params?: GoalsSearchParams) => {
            const query = {
                page: params?.page || 0,
                size: params?.size || 30,
                search: params?.search,
                sort: params?.sort,
                communityId,
                type: params?.type
            };

            const response =
                await fitnessActivityClient.admin.communityGoalAdminControllerGetGoals(
                    query
                );
            return response.data.content;
        },
        {
            defaultData: []
        }
    );

    const createGoal = useWrapRequest(
        async (goalData: CommunityDailyGoalRequestV3Dto) => {
            return fitnessActivityClient.admin.communityGoalAdminControllerCreateCommunityGoal(
                goalData
            );
        },
        {
            successMessage: () => 'Goal created successfully'
        }
    );

    const updateGoal = useWrapRequest(
        async (key: string, goalData: CommunityGoalAdminUpdateRequestDto) => {
            return fitnessActivityClient.admin.communityGoalAdminControllerUpdateCommunityGoal(
                key,
                goalData
            );
        },
        {
            successMessage: () => 'Goal updated successfully'
        }
    );

    const deleteGoal = useWrapRequest(async (key: string) => {
        return fitnessActivityClient.admin.communityGoalAdminControllerDeleteCommunityGoal(
            key
        );
    });

    const handleCreateGoal = React.useCallback(
        async (
            goalData: CommunityDailyGoalRequestV3Dto,
            assignAllUsers: boolean
        ) => {
            try {
                const response = await createGoal.request([goalData]);

                // If assignAllUsers is true, assign all community members to the goal
                if (assignAllUsers && response?.data?.key) {
                    await challengeClient.support.goalSupportControllerAssignCommunityMembers(
                        communityId,
                        response.data.key
                    );
                }

                // Refresh the goals list after successful creation
                await goals.request();
                return true;
            } catch (error) {
                console.error('Error creating goal:', error);
                return false;
            }
        },
        [createGoal, goals, challengeClient, communityId]
    );

    const handleUpdateGoal = React.useCallback(
        async (key: string, goalData: CommunityGoalAdminUpdateRequestDto) => {
            try {
                await updateGoal.request([key, goalData]);
                await goals.request();
                return true;
            } catch (error) {
                console.error('Error updating goal:', error);
                return false;
            }
        },
        [updateGoal, goals]
    );

    const handleDeleteGoal = React.useCallback(
        async (key: string) => {
            try {
                await deleteGoal.request([key]);
                // Refresh the goals list after successful deletion
                await goals.request();
                return true;
            } catch (error) {
                console.error('Error deleting goal:', error);
                return false;
            }
        },
        [deleteGoal, goals]
    );

    return {
        goals,
        setSearchTerm,
        searchParams,
        dispatch,
        handleCreateGoal,
        handleUpdateGoal,
        handleDeleteGoal
    };
}
